import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAdmin } from '@/contexts/AdminContext';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { 
  LogOut, BarChart3, Eye, Users, MousePointerClick, TrendingUp, 
  Calendar, Globe, Smartphone, Monitor, Tablet, ArrowUpRight,
  Images, RefreshCw
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminStatistics = () => {
  const { t, i18n } = useTranslation();
  const { isAdmin, role, hasPermission, logout, loading: authLoading } = useAdmin();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalViews: 0,
    totalRegistrations: 0,
    conversionRate: 0,
    topEvents: [],
    dailyViews: [],
    deviceStats: { desktop: 0, mobile: 0, tablet: 0 },
    browserStats: {},
    recentActivity: []
  });

  const languages = [
    { code: 'it', name: 'IT', flag: 'https://flagcdn.com/w40/it.png' },
    { code: 'en', name: 'EN', flag: 'https://flagcdn.com/w40/gb.png' },
    { code: 'de', name: 'DE', flag: 'https://flagcdn.com/w40/de.png' },
    { code: 'fr', name: 'FR', flag: 'https://flagcdn.com/w40/fr.png' },
    { code: 'es', name: 'ES', flag: 'https://flagcdn.com/w40/es.png' }
  ];

  useEffect(() => {
    if (!isAdmin) {
      navigate('/admin/login');
      return;
    }
    fetchAllStats();
  }, [isAdmin, navigate]);

  const fetchAllStats = async () => {
    setLoading(true);
    try {
      // Fetch view stats
      const viewsResponse = await axios.get(`${API}/events/stats/views`);
      const topEvents = viewsResponse.data.slice(0, 10);
      const totalViews = viewsResponse.data.reduce((sum, e) => sum + (e.view_count || 0), 0);

      // Fetch registrations count
      const regsResponse = await axios.get(`${API}/registrations`);
      const totalRegistrations = regsResponse.data.length;

      // Calculate conversion rate
      const conversionRate = totalViews > 0 ? ((totalRegistrations / totalViews) * 100).toFixed(2) : 0;

      // Fetch analytics dashboard if available
      let analyticsData = {};
      try {
        const analyticsResponse = await axios.get(`${API}/analytics/dashboard`);
        analyticsData = analyticsResponse.data;
      } catch (e) {
        console.log('Analytics endpoint not available yet');
      }

      setStats({
        totalViews,
        totalRegistrations,
        conversionRate,
        topEvents,
        dailyViews: analyticsData.daily_views || [],
        deviceStats: analyticsData.device_stats || { desktop: 0, mobile: 0, tablet: 0 },
        browserStats: analyticsData.browser_stats || {},
        recentActivity: analyticsData.recent_activity || []
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  // Wait for auth context
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center bg-white p-8 rounded-xl shadow-lg">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('admin.dashboard.accessRequired')}</h2>
          <p className="text-gray-600 mb-6">{t('admin.dashboard.loginRequired')}</p>
          <Link 
            to="/admin/login" 
            className="bg-emerald-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-emerald-700 transition-colors inline-block"
          >
            {t('admin.dashboard.goToLogin')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-emerald-600 text-white py-6 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold flex items-center">
                <BarChart3 className="mr-3" size={32} />
                {t('admin.statistics.title')}
              </h1>
              <p className="text-emerald-100 mt-1">
                {role === 'tech' ? t('admin.login.techAccess') : t('admin.login.adminAccess')}
              </p>
            </div>
            <div className="flex items-center gap-4">
              {/* Language Selector */}
              <div className="flex items-center gap-1 bg-emerald-500/50 rounded-lg p-1">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => i18n.changeLanguage(lang.code)}
                    title={lang.name}
                    className={`p-1 rounded transition-all ${
                      i18n.language === lang.code
                        ? 'ring-2 ring-white scale-110 bg-white/30'
                        : 'opacity-70 hover:opacity-100 hover:scale-105'
                    }`}
                  >
                    <img 
                      src={lang.flag} 
                      alt={lang.name}
                      className="w-5 h-4 object-cover rounded-sm"
                    />
                  </button>
                ))}
              </div>
              <button
                onClick={fetchAllStats}
                className="flex items-center bg-emerald-500 text-white px-4 py-2 rounded-lg hover:bg-emerald-400 transition-colors"
                data-testid="refresh-stats-btn"
              >
                <RefreshCw size={18} className="mr-2" />
                {t('common.retry')}
              </button>
              <button
                onClick={handleLogout}
                className="flex items-center bg-white text-emerald-600 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
                data-testid="logout-btn"
              >
                <LogOut className="mr-2" size={20} />
                {t('admin.dashboard.logout')}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Quick Nav for Tech users */}
        {role === 'tech' && (
          <div className="mb-6 flex gap-4">
            <Link
              to="/admin/galleries"
              className="flex items-center bg-pink-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-pink-700 transition-colors shadow-lg"
              data-testid="galleries-link"
            >
              <Images className="mr-2" size={20} />
              {t('admin.dashboard.galleries')}
            </Link>
          </div>
        )}

        {/* Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Total Views */}
          <div className="bg-white rounded-xl shadow-lg p-6" data-testid="total-views-card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">{i18n.language === 'it' ? 'Visualizzazioni Totali' : i18n.language === 'en' ? 'Total Views' : i18n.language === 'de' ? 'Gesamtaufrufe' : i18n.language === 'fr' ? 'Vues Totales' : 'Vistas Totales'}</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">
                  {loading ? '...' : stats.totalViews.toLocaleString()}
                </p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <Eye className="text-blue-600" size={24} />
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-3">{i18n.language === 'it' ? 'Pagine evento visualizzate' : i18n.language === 'en' ? 'Event pages viewed' : i18n.language === 'de' ? 'Aufgerufene Eventseiten' : i18n.language === 'fr' ? 'Pages d\'événements vues' : 'Páginas de eventos vistas'}</p>
          </div>

          {/* Total Registrations */}
          <div className="bg-white rounded-xl shadow-lg p-6" data-testid="total-registrations-card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">{i18n.language === 'it' ? 'Iscrizioni Totali' : i18n.language === 'en' ? 'Total Registrations' : i18n.language === 'de' ? 'Gesamtanmeldungen' : i18n.language === 'fr' ? 'Inscriptions Totales' : 'Inscripciones Totales'}</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">
                  {loading ? '...' : stats.totalRegistrations.toLocaleString()}
                </p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <Users className="text-green-600" size={24} />
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-3">{i18n.language === 'it' ? 'Prenotazioni ricevute' : i18n.language === 'en' ? 'Bookings received' : i18n.language === 'de' ? 'Erhaltene Buchungen' : i18n.language === 'fr' ? 'Réservations reçues' : 'Reservas recibidas'}</p>
          </div>

          {/* Conversion Rate */}
          <div className="bg-white rounded-xl shadow-lg p-6" data-testid="conversion-rate-card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">{i18n.language === 'it' ? 'Tasso Conversione' : i18n.language === 'en' ? 'Conversion Rate' : i18n.language === 'de' ? 'Konversionsrate' : i18n.language === 'fr' ? 'Taux de Conversion' : 'Tasa de Conversión'}</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">
                  {loading ? '...' : `${stats.conversionRate}%`}
                </p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                <TrendingUp className="text-purple-600" size={24} />
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-3">Visite → Iscrizioni</p>
          </div>

          {/* Top Event */}
          <div className="bg-white rounded-xl shadow-lg p-6" data-testid="top-event-card">
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-500">Evento Più Visto</p>
                <p className="text-lg font-bold text-gray-900 mt-1 truncate">
                  {loading ? '...' : (stats.topEvents[0]?.title_it || 'N/A')}
                </p>
              </div>
              <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0 ml-2">
                <MousePointerClick className="text-amber-600" size={24} />
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-3">
              {stats.topEvents[0]?.view_count || 0} visualizzazioni
            </p>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Top Events Table */}
          <div className="bg-white rounded-xl shadow-lg p-6" data-testid="top-events-section">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
              <BarChart3 className="mr-2 text-purple-600" size={24} />
              Eventi Più Visitati
            </h2>
            
            {loading ? (
              <div className="animate-pulse space-y-3">
                {[1,2,3,4,5].map(i => (
                  <div key={i} className="h-12 bg-gray-200 rounded"></div>
                ))}
              </div>
            ) : stats.topEvents.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-2 text-xs font-semibold text-gray-500 uppercase">#</th>
                      <th className="text-left py-3 px-2 text-xs font-semibold text-gray-500 uppercase">Evento</th>
                      <th className="text-right py-3 px-2 text-xs font-semibold text-gray-500 uppercase">Visite</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.topEvents.map((event, index) => (
                      <tr key={event.id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-3 px-2">
                          <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${
                            index === 0 ? 'bg-yellow-400 text-yellow-900' :
                            index === 1 ? 'bg-gray-300 text-gray-700' :
                            index === 2 ? 'bg-amber-600 text-white' :
                            'bg-gray-100 text-gray-600'
                          }`}>
                            {index + 1}
                          </span>
                        </td>
                        <td className="py-3 px-2">
                          <span className="text-sm font-medium text-gray-900 line-clamp-1">
                            {event.title_it}
                          </span>
                        </td>
                        <td className="py-3 px-2 text-right">
                          <span className="inline-flex items-center text-sm font-bold text-purple-600">
                            <Eye size={14} className="mr-1" />
                            {event.view_count || 0}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">Nessun dato disponibile</p>
            )}
            
            <p className="text-xs text-gray-500 mt-4 pt-3 border-t">
              Usa questi dati per ottimizzare le campagne pubblicitarie.
              Eventi con poche visite potrebbero necessitare più promozione.
            </p>
          </div>

          {/* Device Statistics */}
          <div className="bg-white rounded-xl shadow-lg p-6" data-testid="device-stats-section">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
              <Smartphone className="mr-2 text-blue-600" size={24} />
              Dispositivi Utilizzati
            </h2>
            
            {loading ? (
              <div className="animate-pulse space-y-4">
                {[1,2,3].map(i => (
                  <div key={i} className="h-16 bg-gray-200 rounded"></div>
                ))}
              </div>
            ) : (
              <div className="space-y-4">
                {/* Desktop */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center">
                    <Monitor className="text-gray-600 mr-3" size={24} />
                    <div>
                      <p className="font-semibold text-gray-900">Desktop</p>
                      <p className="text-xs text-gray-500">Computer e laptop</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-gray-900">{stats.deviceStats.desktop || 0}</p>
                    <p className="text-xs text-gray-500">visite</p>
                  </div>
                </div>

                {/* Mobile */}
                <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
                  <div className="flex items-center">
                    <Smartphone className="text-blue-600 mr-3" size={24} />
                    <div>
                      <p className="font-semibold text-gray-900">Mobile</p>
                      <p className="text-xs text-gray-500">Smartphone</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-blue-600">{stats.deviceStats.mobile || 0}</p>
                    <p className="text-xs text-gray-500">visite</p>
                  </div>
                </div>

                {/* Tablet */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center">
                    <Tablet className="text-gray-600 mr-3" size={24} />
                    <div>
                      <p className="font-semibold text-gray-900">Tablet</p>
                      <p className="text-xs text-gray-500">iPad e tablet</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-gray-900">{stats.deviceStats.tablet || 0}</p>
                    <p className="text-xs text-gray-500">visite</p>
                  </div>
                </div>
              </div>
            )}

            <p className="text-xs text-gray-500 mt-4 pt-3 border-t">
              Se la maggior parte del traffico proviene da mobile, 
              assicurati che il sito sia ottimizzato per dispositivi mobili.
            </p>
          </div>
        </div>

        {/* Insight Section */}
        <div className="mt-8 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-xl shadow-lg p-6 text-white" data-testid="insights-section">
          <h2 className="text-xl font-bold mb-4 flex items-center">
            <TrendingUp className="mr-2" size={24} />
            Suggerimenti per Migliorare
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white/10 backdrop-blur rounded-lg p-4">
              <h3 className="font-semibold mb-2">Aumenta le Conversioni</h3>
              <p className="text-sm text-emerald-100">
                Il tasso di conversione attuale è {stats.conversionRate}%. 
                Prova a migliorare le descrizioni degli eventi e aggiungere più foto.
              </p>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-lg p-4">
              <h3 className="font-semibold mb-2">Promuovi gli Eventi</h3>
              <p className="text-sm text-emerald-100">
                Gli eventi con meno visite potrebbero beneficiare di campagne 
                social mirate su Facebook e Instagram.
              </p>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-lg p-4">
              <h3 className="font-semibold mb-2">Galleria Foto</h3>
              <p className="text-sm text-emerald-100">
                Aggiungere foto di qualità alla galleria aumenta la fiducia 
                dei clienti e le conversioni.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminStatistics;
